import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import HttpService from "../services/httpService";
import {
    Button,
    TextField,
    Paper,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";

const emptyForm = { text: "", hours: "", color: "#F47E46" };

const TatInternalTicket = (props) => {
    const [tickets, setTickets] = useState([]);
    const [openForm, setOpenForm] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    useEffect(() => {
        fetchTickets();
        // eslint-disable-next-line
    }, []);

    const fetchTickets = async () => {
        props.setLoading(true);
        try {
            const res = await HttpService.getTatInternalTickets();
            setTickets(res?.data?.data || []);
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? "Something went wrong", "error");
        }
        props.setLoading(false);
    };

    const openAddForm = () => {
        setFormData(emptyForm);
        setEditingId(null);
        setOpenForm(true);
    };

    const openEditForm = (ticket) => {
        setFormData({ text: ticket.text, hours: ticket.hours, color: ticket.color });
        setEditingId(ticket._id);
        setOpenForm(true);
    };

    const closeForm = () => {
        setOpenForm(false);
        setFormData(emptyForm);
        setEditingId(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const isHoursMultipleOf24 = formData.hours !== "" && Number(formData.hours) > 0 && Number(formData.hours) % 24 === 0;
    const isFormValid = formData.text.trim() !== "" && isHoursMultipleOf24 && !!formData.color;

    const handleSubmit = async () => {
        if (!isFormValid) {
            props.setSnackInfo(
                !isHoursMultipleOf24
                    ? "Hours must be a multiple of 24 (24, 48, 72, ...)"
                    : "Please fill text, hours and color correctly",
                "warning"
            );
            return;
        }
        props.setLoading(true);
        try {
            const payload = {
                text: formData.text.trim(),
                hours: Number(formData.hours),
                color: formData.color
            };
            if (editingId) {
                await HttpService.updateTatInternalTicket(editingId, payload);
                props.setSnackInfo("Ticket updated successfully", "success");
            } else {
                await HttpService.createTatInternalTicket(payload);
                props.setSnackInfo("Ticket created successfully", "success");
            }
            closeForm();
            fetchTickets();
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? "Something went wrong", "error");
        }
        props.setLoading(false);
    };

    const confirmDelete = (id) => {
        setDeleteId(id);
    };

    const handleDelete = async () => {
        props.setLoading(true);
        try {
            await HttpService.deleteTatInternalTicket(deleteId);
            props.setSnackInfo("Ticket deleted successfully", "success");
            setDeleteId(null);
            fetchTickets();
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? "Something went wrong", "error");
        }
        props.setLoading(false);
    };

    return (
        <Box style={{ padding: 20 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">TAT Internal Tickets</Typography>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openAddForm}>
                    Add Ticket
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow style={{ backgroundColor: "#f0f8ff" }}>
                            <TableCell style={{ fontWeight: "bold" }}>Sl No.</TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>Text</TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>Hours</TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>Color</TableCell>
                            <TableCell style={{ fontWeight: "bold" }} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tickets.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">No Records Found</TableCell>
                            </TableRow>
                        ) : (
                            tickets.map((ticket, i) => (
                                <TableRow key={ticket._id}>
                                    <TableCell>{i + 1}</TableCell>
                                    <TableCell>{ticket.text}</TableCell>
                                    <TableCell>{ticket.hours}</TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <Box
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    backgroundColor: ticket.color,
                                                    border: "1px solid #ccc",
                                                    marginRight: 8
                                                }}
                                            />
                                            {ticket.color}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => openEditForm(ticket)}>
                                            <EditIcon fontSize="small" style={{ color: "#1C375D" }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => confirmDelete(ticket._id)}>
                                            <DeleteIcon fontSize="small" style={{ color: "#d32f2f" }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add / Edit Dialog */}
            <Dialog open={openForm} onClose={closeForm} fullWidth maxWidth="xs">
                <DialogTitle>{editingId ? "Edit Ticket" : "Add Ticket"}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Text"
                        name="text"
                        value={formData.text}
                        onChange={handleChange}
                        margin="dense"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Hours"
                        name="hours"
                        type="number"
                        value={formData.hours}
                        onChange={handleChange}
                        margin="dense"
                        inputProps={{ min: 24, step: 24 }}
                        required
                        error={formData.hours !== "" && !isHoursMultipleOf24}
                        helperText={
                            formData.hours !== "" && !isHoursMultipleOf24
                                ? "Must be a multiple of 24 (24, 48, 72, ...)"
                                : "Enter a multiple of 24"
                        }
                    />
                    <Box mt={2}>
                        <Typography variant="body2" style={{ marginBottom: 6 }}>Color</Typography>
                        <input
                            type="color"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                            style={{ width: 60, height: 36, border: "none", cursor: "pointer" }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeForm} color="secondary">Cancel</Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!isFormValid}>
                        {editingId ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation Dialog */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
                <DialogTitle>Delete Ticket</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this ticket?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)} color="secondary">Cancel</Button>
                    <Button onClick={handleDelete} color="primary" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default connect(null, { setLoading, setSnackInfo })(TatInternalTicket);
